import React, { useState, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarDaysIcon,
} from '@heroicons/react/20/solid';

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const cn = (...c) => c.filter(Boolean).join(' ');

const WeekSlider = ({
  onWeekChange,
  initialDate,
  weekOfStartMonday = true,
  useNowAsEndDate = false,
}) => {
  const [currentDate, setCurrentDate] = useState(dayjs(initialDate || dayjs()));
  const today = dayjs();

  const getWeekStart = (date) =>
    weekOfStartMonday ? date.startOf('isoWeek') : date.subtract(6, 'day');
  const getWeekEnd = (date) => {
    if (useNowAsEndDate) return date;
    return weekOfStartMonday
      ? date.endOf('week')
      : date.endOf('week').subtract(1, 'day');
  };

  const startOfWeek = getWeekStart(currentDate);
  const endOfWeek = getWeekEnd(currentDate);

  const isCurrentWeek = useMemo(
    () =>
      today.isSameOrAfter(startOfWeek, 'day') &&
      today.isSameOrBefore(endOfWeek, 'day'),
    [today, startOfWeek, endOfWeek],
  );

  const nextDisabled = useMemo(
    () => currentDate.add(7, 'day').isAfter(today, 'day'),
    [currentDate, today],
  );

  useEffect(() => {
    onWeekChange(startOfWeek, endOfWeek);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate]);

  const prevWeek = () => {
    const newDate = currentDate.subtract(7, 'day');
    setCurrentDate(newDate);
    onWeekChange(getWeekStart(newDate), getWeekEnd(newDate));
  };

  const nextWeek = () => {
    const newDate = currentDate.add(7, 'day');
    if (newDate.isSameOrBefore(today, 'day')) {
      setCurrentDate(newDate);
      onWeekChange(getWeekStart(newDate), getWeekEnd(newDate));
    }
  };

  const sameMonth = startOfWeek.isSame(endOfWeek, 'month');
  const dateLabel = sameMonth
    ? `${startOfWeek.format('MMM D')} – ${endOfWeek.format('D')}`
    : `${startOfWeek.format('MMM D')} – ${endOfWeek.format('MMM D')}`;
  const navBtn =
    'group inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 transition hover:bg-blue-500/10 hover:text-blue-600 active:scale-95 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-500 dark:text-slate-400 dark:hover:bg-sky-400/10 dark:hover:text-sky-400';

  return (
    <div
      className="flex h-10 w-full items-center justify-between gap-1 self-start rounded-md border border-slate-200 bg-white px-1.5 shadow-sm dark:border-slate-700 dark:bg-slate-800 sm:inline-flex sm:w-fit sm:max-w-full sm:justify-start"
      aria-label="Week selector"
    >
      <button
        type="button"
        onClick={prevWeek}
        aria-label="Previous week"
        className={navBtn}
      >
        <ChevronLeftIcon className="h-4 w-4 transition group-hover:-translate-x-0.5" />
      </button>

      <div className="flex items-center gap-2 px-2">
        <CalendarDaysIcon className="h-4 w-4 shrink-0 text-blue-600 dark:text-sky-400" />
        <span className="whitespace-nowrap tabular-nums text-sm font-semibold tracking-tight text-slate-900 dark:text-slate-100">
          {isCurrentWeek ? 'This Week' : dateLabel}
        </span>
      </div>

      <button
        type="button"
        onClick={nextWeek}
        disabled={nextDisabled}
        aria-label="Next week"
        className={cn(navBtn)}
      >
        <ChevronRightIcon className="h-4 w-4 transition group-hover:translate-x-0.5 group-disabled:translate-x-0" />
      </button>
    </div>
  );
};

export default WeekSlider;
